import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X, Loader2, HelpCircle, Trash2 } from 'lucide-react'
import { useUIStore } from '../../stores/uiStore'

const TYPE_STYLES = {
  success: { icon: CheckCircle2, cls: 'border-emerald-200 bg-emerald-50 text-emerald-900', iconCls: 'text-emerald-600' },
  error:   { icon: AlertCircle, cls: 'border-red-200 bg-red-50 text-red-900', iconCls: 'text-red-600' },
  warning: { icon: AlertTriangle, cls: 'border-amber-200 bg-amber-50 text-amber-900', iconCls: 'text-amber-600' },
  info:    { icon: Info, cls: 'border-blue-200 bg-blue-50 text-blue-900', iconCls: 'text-blue-600' },
}

function Toast({ t, onClose }) {
  const s = TYPE_STYLES[t.type] || TYPE_STYLES.info
  const Icon = s.icon
  return (
    <div className={`flex items-start gap-3 min-w-[280px] max-w-md p-3 pr-2 rounded-xl border shadow-lg ${s.cls}`}>
      <Icon size={18} className={`${s.iconCls} shrink-0 mt-0.5`} />
      {/* onClick verilmişse gövde tıklanabilir (bildirimin sayfasına gider); çarpı yalnız kapatır */}
      <div className={`flex-1 min-w-0 ${t.onClick ? 'cursor-pointer' : ''}`}
           onClick={t.onClick ? () => { t.onClick(); onClose() } : undefined}>
        {t.title && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold">{t.title}</span>
            {t.badge?.label && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${t.badge.cls || ''}`}>
                {t.badge.label}
              </span>
            )}
          </div>
        )}
        {t.message && <div className="text-xs text-gray-700 mt-0.5 whitespace-pre-wrap break-words">{t.message}</div>}
      </div>
      <button onClick={onClose}
              className="p-1 rounded hover:bg-black/5 text-gray-500 shrink-0">
        <X size={14} />
      </button>
    </div>
  )
}

function ConfirmDialog({ state, onClose }) {
  const [mounted, setMounted] = useState(false)
  const cancelBtnRef = useRef(null)

  useEffect(() => {
    if (state) {
      setMounted(false)
      const t = requestAnimationFrame(() => setMounted(true))
      cancelBtnRef.current?.focus()
      const onKey = (e) => {
        if (e.key === 'Escape') onClose(false)
        else if (e.key === 'Enter') onClose(true)
      }
      window.addEventListener('keydown', onKey)
      return () => { cancelAnimationFrame(t); window.removeEventListener('keydown', onKey) }
    }
  }, [state, onClose])

  if (!state) return null
  const { title, message, variant = 'default', confirmLabel, cancelLabel, loading } = state
  const isDanger = variant === 'danger'

  const Icon = isDanger ? Trash2 : HelpCircle
  const iconWrap = isDanger
    ? 'bg-red-50 text-red-600'
    : 'bg-primary-50 text-primary-600'
  const confirmBtn = isDanger
    ? 'bg-red-500 hover:bg-red-600 focus:ring-red-400'
    : 'bg-primary-500 hover:bg-primary-600 focus:ring-primary-400'

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-opacity duration-150 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      onClick={() => !loading && onClose(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-150 ${mounted ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
      >
        <div className="flex items-start gap-4 p-6">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${iconWrap}`}>
            <Icon size={20} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            {title && <h3 className="text-base font-semibold text-gray-900">{title}</h3>}
            {message && <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap">{message}</p>}
          </div>
          <button
            onClick={() => !loading && onClose(false)}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            ref={cancelBtnRef}
            onClick={() => onClose(false)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {cancelLabel || 'Vazgeç'}
          </button>
          <button
            onClick={() => onClose(true)}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 focus:outline-none focus:ring-2 ${confirmBtn}`}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel || 'Onayla'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function NotificationCenter() {
  const toasts = useUIStore((s) => s.toasts)
  const removeToast = useUIStore((s) => s.removeToast)
  const confirmState = useUIStore((s) => s.confirmState)
  const closeConfirm = useUIStore((s) => s.closeConfirm)

  return (
    <>
      <div className="fixed top-4 right-4 z-[90] space-y-2 pointer-events-none">
        <div className="space-y-2 pointer-events-auto">
          {toasts.map((t) => (
            <Toast key={t.id} t={t} onClose={() => removeToast(t.id)} />
          ))}
        </div>
      </div>
      <ConfirmDialog state={confirmState} onClose={closeConfirm} />
    </>
  )
}
